// La fase 3 de la generación del informe sobre TanStack Query: el catálogo de
// secciones, la corrida de redacción (con polling mientras corre) y las
// acciones sobre una sección.

import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { hechosKey } from "@/hooks/useGeneracion";
import { ApiError } from "@/lib/api";
import {
  accionDeLaSeccionRequest,
  editarTextoRequest,
  errorDeLaAccion,
  fetchComparacion,
  fetchCorridaDeLaRedaccion,
  fetchRevisiones,
  fetchSecciones,
  intervaloDeLaRedaccion,
  redactarFinalesRequest,
  redactarPendientesRequest,
  regenerarRequest,
  type AccionDeLaSeccion,
  type CorridaDeLaRedaccion,
  type DatosDeSecciones,
  type SeccionDelInforme,
} from "@/lib/redaccion";

export const seccionesKey = (informeId: number) => ["generacion", informeId, "secciones"] as const;
export const corridaDeLaRedaccionKey = (informeId: number) => ["generacion", informeId, "redaccion"] as const;
export const revisionesKey = (informeId: number, itemId: number) =>
  ["generacion", informeId, "secciones", itemId, "revisiones"] as const;

const valido = (informeId: number) => Number.isFinite(informeId) && informeId > 0;

export function useSecciones(informeId: number, habilitada = true) {
  return useQuery({
    queryKey: seccionesKey(informeId),
    queryFn: () => fetchSecciones(informeId),
    enabled: habilitada && valido(informeId),
  });
}

/**
 * La última corrida de redacción. Sondea cada ~3 s mientras está `en_curso` y
 * se apaga sola al terminar (la función se reevalúa con cada respuesta).
 */
export function useCorridaDeLaRedaccion(informeId: number, habilitada = true) {
  return useQuery({
    queryKey: corridaDeLaRedaccionKey(informeId),
    queryFn: () => fetchCorridaDeLaRedaccion(informeId),
    enabled: habilitada && valido(informeId),
    refetchInterval: (query) => intervaloDeLaRedaccion(query.state.data),
  });
}

/**
 * Las dos lecturas de la fase y, si se pide, la sincronización entre ellas:
 * cuando la corrida avanza (una sección más redactada, o terminó) el catálogo
 * se vuelve a leer, porque es ahí donde está el texto y el estado de cada una.
 *
 * `sincronizar` es falso cuando OTRO componente ya la hace (la pantalla pasa
 * `false` mientras la fase abierta es la de redacción): dos efectos
 * invalidando a la vez cancelarían uno la lectura del otro.
 */
export function useRedaccionSincronizada(informeId: number, habilitada: boolean, sincronizar: boolean) {
  const queryClient = useQueryClient();
  const secciones = useSecciones(informeId, habilitada);
  const corrida = useCorridaDeLaRedaccion(informeId, habilitada);

  const marca = corrida.data
    ? `${corrida.data.corrida_id}:${corrida.data.estado}:${corrida.data.contadores.redactadas}:${corrida.data.contadores.con_error}`
    : null;

  // La primera marca es la de la carga inicial: el catálogo se acaba de pedir
  // y volver a pedirlo no trae nada. Solo un CAMBIO de marca es progreso.
  const anterior = useRef<string | null>(null);

  useEffect(() => {
    const huboAvance = anterior.current !== null && marca !== null && marca !== anterior.current;
    anterior.current = marca;

    if (!sincronizar || !huboAvance) return;

    void queryClient.invalidateQueries({ queryKey: seccionesKey(informeId) });
    void queryClient.invalidateQueries({ queryKey: hechosKey(informeId) });
  }, [sincronizar, marca, informeId, queryClient]);

  return { secciones, corrida };
}

/** Reemplaza UNA sección en la cache con la que devolvió el servidor. */
function sembrar(datos: DatosDeSecciones | undefined, nueva: SeccionDelInforme): DatosDeSecciones | undefined {
  if (datos === undefined) return datos;

  return { ...datos, secciones: datos.secciones.map((s) => (s.item_id === nueva.item_id ? nueva : s)) };
}

/**
 * Tras cualquier cambio de una sección se relee el catálogo (el resumen, las
 * dependientes marcadas desactualizadas) y la hoja de hechos (`citada_por`).
 */
function useRefrescar(informeId: number) {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: seccionesKey(informeId) }),
      queryClient.invalidateQueries({ queryKey: hechosKey(informeId) }),
    ]);
}

/**
 * Un 409 `redaccion_en_curso` NO es un error: alguien ya la lanzó y lo que
 * corresponde es mostrar esa corrida. Devuelve `null` para que el llamador
 * relea en vez de avisar de una falla que no ocurrió.
 */
async function tolerandoLaCorridaEnCurso(pedir: () => Promise<CorridaDeLaRedaccion>): Promise<CorridaDeLaRedaccion | null> {
  try {
    return await pedir();
  } catch (error) {
    if (error instanceof ApiError && error.status === 409 && errorDeLaAccion(error).codigo === "redaccion_en_curso") {
      return null;
    }
    throw error;
  }
}

function useAlAbrirUnaCorrida(informeId: number) {
  const queryClient = useQueryClient();
  const refrescar = useRefrescar(informeId);

  return (corrida: CorridaDeLaRedaccion | null) => {
    if (corrida) queryClient.setQueryData(corridaDeLaRedaccionKey(informeId), corrida);
    else void queryClient.invalidateQueries({ queryKey: corridaDeLaRedaccionKey(informeId) });

    return refrescar();
  };
}

export function useRedactarPendientes(informeId: number) {
  const alAbrir = useAlAbrirUnaCorrida(informeId);

  return useMutation({
    mutationFn: () => tolerandoLaCorridaEnCurso(() => redactarPendientesRequest(informeId)),
    onSuccess: alAbrir,
  });
}

export function useRedactarFinales(informeId: number) {
  const alAbrir = useAlAbrirUnaCorrida(informeId);
  const refrescar = useRefrescar(informeId);

  return useMutation({
    mutationFn: () => tolerandoLaCorridaEnCurso(() => redactarFinalesRequest(informeId)),
    onSuccess: alAbrir,
    // `finales_bloqueadas`: lo que se ve está viejo, algo cambió desde que se cargó.
    onError: refrescar,
  });
}

export function useRegenerarSeccion(informeId: number) {
  const alAbrir = useAlAbrirUnaCorrida(informeId);

  return useMutation({
    mutationFn: ({ itemId, instruccion }: { itemId: number; instruccion: string | null }) =>
      tolerandoLaCorridaEnCurso(() => regenerarRequest(informeId, itemId, instruccion)),
    onSuccess: alAbrir,
  });
}

export function useEditarTexto(informeId: number) {
  const queryClient = useQueryClient();
  const refrescar = useRefrescar(informeId);

  return useMutation({
    mutationFn: ({ itemId, texto }: { itemId: number; texto: string }) => editarTextoRequest(informeId, itemId, texto),
    onSuccess: (seccion) => {
      queryClient.setQueryData<DatosDeSecciones>(seccionesKey(informeId), (datos) => sembrar(datos, seccion));
      void queryClient.invalidateQueries({ queryKey: revisionesKey(informeId, seccion.item_id) });

      return refrescar();
    },
    onError: refrescar,
  });
}

export function useAccionDeLaSeccion(informeId: number) {
  const queryClient = useQueryClient();
  const refrescar = useRefrescar(informeId);

  return useMutation({
    mutationFn: ({ itemId, accion }: { itemId: number; accion: AccionDeLaSeccion }) =>
      accionDeLaSeccionRequest(informeId, itemId, accion),
    onSuccess: (seccion) => {
      queryClient.setQueryData<DatosDeSecciones>(seccionesKey(informeId), (datos) => sembrar(datos, seccion));

      return refrescar();
    },
    // Un 409 dice que el estado que se ve ya no es el del servidor.
    onError: refrescar,
  });
}

export function useRevisiones(informeId: number, itemId: number, habilitada: boolean) {
  return useQuery({
    queryKey: revisionesKey(informeId, itemId),
    queryFn: () => fetchRevisiones(informeId, itemId),
    enabled: habilitada && valido(informeId),
  });
}

export function useComparacion(informeId: number, itemId: number, a: number | null, b: number | null) {
  return useQuery({
    queryKey: [...revisionesKey(informeId, itemId), "comparar", a, b] as const,
    queryFn: () => fetchComparacion(informeId, itemId, a as number, b as number),
    enabled: valido(informeId) && a !== null && b !== null && a !== b,
  });
}
